'use client';

import { useEffect, useRef, useState } from 'react';
import type { LiveSeatUpdate } from './seat-transform';

/**
 * Shared SSE broker — keeps at most one `EventSource` per
 * `(apiBaseUrl, ugmAddress, gridId)` triple, regardless of how many
 * `<TakeoverGrid live="sse">` mounts consume it on the page.
 *
 * This is the single-connection invariant the main app enforces inside
 * `use-lobby-grid-page.ts`; the SDK reproduces it at module scope so a
 * partner rendering 3+ boards doesn't fan us out on our `/api/v1/live/*`
 * endpoint.
 */

type BrokerListener = (update: BrokerMessage) => void;

interface BrokerMessage {
  type: 'seats' | 'error' | 'open';
  seats?: LiveSeatUpdate[];
  error?: string;
}

interface BrokerEntry {
  source: EventSource;
  listeners: Set<BrokerListener>;
  closed: boolean;
}

const brokers: Map<string, BrokerEntry> = new Map();

function brokerKey(apiBaseUrl: string, ugmAddress: string, gridId: string): string {
  return `${apiBaseUrl}::${ugmAddress.toLowerCase()}::${gridId}`;
}

function subscribe(
  apiBaseUrl: string,
  ugmAddress: string,
  gridId: string,
  listener: BrokerListener,
): () => void {
  const key = brokerKey(apiBaseUrl, ugmAddress, gridId);
  let entry = brokers.get(key);
  if (!entry) {
    const base = apiBaseUrl.replace(/\/$/, '');
    const path = `${base}/api/v1/live/grid/${ugmAddress}/${gridId}`;
    const source = new EventSource(path);
    const listeners = new Set<BrokerListener>();
    entry = { source, listeners, closed: false };
    brokers.set(key, entry);

    source.onopen = () => {
      entry!.listeners.forEach((fn) => fn({ type: 'open' }));
    };

    source.onerror = () => {
      entry!.listeners.forEach((fn) =>
        fn({ type: 'error', error: 'SSE connection error' }),
      );
    };

    source.onmessage = (ev) => {
      try {
        const parsed = JSON.parse(ev.data) as {
          seats?: LiveSeatUpdate[];
          type?: string;
        };
        if (Array.isArray(parsed.seats)) {
          entry!.listeners.forEach((fn) => fn({ type: 'seats', seats: parsed.seats }));
        }
      } catch {
        // Ignore malformed frame — server occasionally sends heartbeats.
      }
    };
  }

  entry.listeners.add(listener);

  return () => {
    const current = brokers.get(key);
    if (!current) return;
    current.listeners.delete(listener);
    if (current.listeners.size === 0 && !current.closed) {
      current.closed = true;
      try {
        current.source.close();
      } catch {
        // noop — EventSource.close is idempotent in practice.
      }
      brokers.delete(key);
    }
  };
}

export interface UseLiveGridOptions {
  apiBaseUrl: string;
  ugmAddress: string;
  gridId: string;
  enabled?: boolean;
}

export interface UseLiveGridResult {
  liveSeats: LiveSeatUpdate[] | null;
  isConnected: boolean;
  error: string | null;
}

/**
 * Subscribe to the live-grid SSE stream. Returns the latest `seats` payload
 * as reported by the server. The full grid reconciliation (merging into a
 * baseline `SeatData[]`) is done by the `useSeats` hook.
 *
 * **Cost note.** Each `(ugm, gridId)` pair opens one `EventSource` per
 * browser page, shared via the module-level broker above. The `live` prop
 * on `<TakeoverGrid />` defaults to `'poll'` so callers must opt into SSE
 * explicitly.
 */
export function useLiveGrid({
  apiBaseUrl,
  ugmAddress,
  gridId,
  enabled = true,
}: UseLiveGridOptions): UseLiveGridResult {
  const [liveSeats, setLiveSeats] = useState<LiveSeatUpdate[] | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled || !ugmAddress || !gridId) return;
    if (typeof window === 'undefined' || typeof EventSource === 'undefined') return;

    const unsubscribe = subscribe(apiBaseUrl, ugmAddress, gridId, (msg) => {
      if (!mountedRef.current) return;
      if (msg.type === 'open') {
        setIsConnected(true);
        setError(null);
      } else if (msg.type === 'error') {
        setIsConnected(false);
        setError(msg.error ?? 'SSE error');
      } else if (msg.type === 'seats' && msg.seats) {
        setLiveSeats(msg.seats);
      }
    });

    return () => {
      unsubscribe();
      setIsConnected(false);
    };
  }, [apiBaseUrl, ugmAddress, gridId, enabled]);

  return { liveSeats, isConnected, error };
}
